import { useDebounced } from "@web/lib/useDebounced";
import { useEffect, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { CalendarCheck, Plus } from "lucide-react";
import { formatCents } from "@shared/money";
import { bookingHasEffectiveHold } from "@shared/validation/holdExpiry";
import { bookingTitle, fmtDate, STATUS_LABEL, statusLabel, statusTone, todayIn } from "@web/lib/format";
import { useNavAction } from "@web/lib/navAction";
import { bookingsQuery, type BookingListItem } from "@web/lib/queries";
import { useMe } from "@web/lib/session";
import { useBatchValidation, type RecordEvaluation } from "@web/lib/validation";
import { Button, ChipStrip, CollectionHeader, ColorDot, EmptyState, Inline, Page, Row, RowGroup, SearchBar, SelectChip, Skeleton, Stack, StatusChip, Text, ValidationBadge } from "@web/ui";
import { NewBookingSheet } from "./sheets/NewBookingSheet";

const FILTERS: Array<{ key: string; label: string; status?: string; upcoming?: boolean }> = [
  { key: "upcoming", label: "Upcoming", status: "pending,contract_sent,signed,booked", upcoming: true },
  { key: "pending", label: STATUS_LABEL.pending, status: "pending" },
  { key: "booked", label: STATUS_LABEL.booked, status: "booked" },
  { key: "completed", label: STATUS_LABEL.completed, status: "completed" },
  { key: "cancelled", label: STATUS_LABEL.cancelled, status: "cancelled" },
  { key: "all", label: "All" },
];

export type BookingsSearch = { reviewStatus?: "unreviewed" | "reviewed" | "approved"; awaitingCountersign?: boolean; filter?: string; q?: string };

/** The shareable address contract: unknown keys and oversized values are dropped,
 * and an absent key means the screen's own default. */
export function parseBookingsSearch(raw: Record<string, unknown>): BookingsSearch {
  const out: BookingsSearch = {};
  if (raw.reviewStatus === "unreviewed" || raw.reviewStatus === "reviewed" || raw.reviewStatus === "approved") out.reviewStatus = raw.reviewStatus;
  if (raw.awaitingCountersign === true || raw.awaitingCountersign === "1") out.awaitingCountersign = true;
  if (FILTERS.some((x) => x.key === raw.filter)) out.filter = raw.filter as string;
  if (typeof raw.q === "string" && raw.q && raw.q.length <= 200) out.q = raw.q;
  return out;
}


/** Bookings (SPEC §6.1 item 7): search, status chips, rows with StatusChip + balance-due badge. */
export function Bookings() {
  const me = useMe();
  const navigate = useNavigate();
  // Status chip and search live in the URL, so working through a long list —
  // open a booking, Back, open the next — keeps the list the person built
  // (docs/plans/RESPONSIVE-STANDARD.md).
  const search = useSearch({ from: "/app/bookings" });
  const setSearch = (next: BookingsSearch) => navigate({ to: "/bookings", search: { ...search, ...next }, replace: true });
  const queue = search.awaitingCountersign ? "Agreements awaiting countersign" : search.reviewStatus === "unreviewed" ? "Bookings to review" : search.reviewStatus === "reviewed" ? "Bookings to approve" : search.reviewStatus === "approved" ? "Approved bookings" : null;
  const today = todayIn(me.organization.timezone);
  const q = search.q ?? "";
  const filter = search.filter ?? "upcoming";
  const [adding, setAdding] = useState(false);
  useNavAction({ label: "New booking", icon: <Plus aria-hidden size={26} />, onClick: () => setAdding(true) });
  const debounced = useDebounced(q);
  const f = FILTERS.find((x) => x.key === filter)!;
  const list = useInfiniteQuery(bookingsQuery({ q: debounced || undefined, status: queue ? undefined : f.status, from: !queue && f.upcoming && !debounced ? today : undefined, reviewStatus: search.reviewStatus, awaitingCountersign: search.awaitingCountersign ? "1" : undefined }));
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  const checks = useBatchValidation(items.map(item => ({ entityType: "booking", id: item.id })));

  return (
    <Page variant="collection">
      <Stack gap={3}>
        {queue && <Inline justify="between" wrap><Text variant="headline">{queue}</Text><Button variant="secondary" fullWidth={false} onClick={() => setSearch({ reviewStatus: undefined, awaitingCountersign: undefined })}>Clear queue filter</Button></Inline>}
        <SearchBar value={q} onChange={(value) => setSearch({ q: value || undefined })} placeholder="Search client, event, phone" label="Search bookings" />
        <ChipStrip label="Status">
          {FILTERS.map((x) => (
            // Picking a status leaves whichever review queue brought the person
            // here, but keeps what they typed.
            <SelectChip key={x.key} selected={!queue && filter === x.key} onClick={() => setSearch({ filter: x.key === "upcoming" ? undefined : x.key, reviewStatus: undefined, awaitingCountersign: undefined })}>
              {x.label}
            </SelectChip>
          ))}
        </ChipStrip>
        {list.isError ? <EmptyState title="Bookings unavailable" body="The list could not be loaded. Your filters have been retained." action={<Button onClick={() => void list.refetch()}>Retry bookings</Button>} /> : list.isLoading && !list.data ? (
          <Skeleton count={6} />
        ) : items.length === 0 ? (
          <EmptyState icon={CalendarCheck} title={debounced ? "No matches" : queue ? "Nothing waiting here" : "No bookings here"} body={debounced ? "Try a name, phone or event." : queue ? "No visible bookings currently match this queue." : "Tap + to create one."} />
        ) : (
          <>
            <RowGroup>
              <CollectionHeader columns={["Event / client", "Status / balance"]} />
              {items.map((b) => (
                <BookingRow key={b.id} b={b} today={today} validation={checks.data?.records.find(record => record.entity.type === "booking" && record.entity.id === b.id)} unavailable={checks.isError} onClick={() => search.awaitingCountersign && b.contractId ? navigate({ to: "/contracts/$id/countersign", params: { id: b.contractId } }) : navigate({ to: "/bookings/$id", params: { id: b.id } })} />
              ))}
            </RowGroup>
            {list.hasNextPage ? (
              <Stack gap={2}>
                <Text variant="caption" tone="tertiary" align="center">
                  Showing {items.length} of more…
                </Text>
                <Button variant="secondary" loading={list.isFetchingNextPage} onClick={() => list.fetchNextPage()}>
                  Load more
                </Button>
              </Stack>
            ) : (
              <Text variant="caption" tone="tertiary" align="center">
                {items.length} booking{items.length === 1 ? "" : "s"}
              </Text>
            )}
          </>
        )}
      </Stack>
      {adding && <NewBookingSheet open onClose={() => setAdding(false)} />}
    </Page>
  );
}

function BookingRow({ b, today, onClick, validation, unavailable }: { b: BookingListItem; today: string; onClick: () => void; validation?: RecordEvaluation; unavailable?: boolean }) {
  const client = bookingTitle(b);
  const overdue = b.balanceDue > 0 && b.balanceDueDate !== null && b.balanceDueDate < today && b.status !== "cancelled";
  const live = b.status !== "cancelled";
  return (
    <Row
      variant="record"
      icon={<ColorDot color={b.venueColor} />}
      title={client}
      subtitle={`${b.venueShortName ?? ""} · ${fmtDate(b.eventDate, { weekday: true })}${b.eventName ? ` · ${b.eventName}` : ""}`}
      trailing={
        <Stack gap={1} align="end">
          <StatusChip tone={bookingHasEffectiveHold(b, validation?.checkedAt ?? new Date()) ? "warn" : statusTone(b.status)}>{bookingHasEffectiveHold(b, validation?.checkedAt ?? new Date()) ? "Hold" : statusLabel(b.status)}</StatusChip>
          <ValidationBadge result={validation} unavailable={unavailable} />
          {live && b.legacyPaymentStatus === "Unknown" ? (
            <StatusChip tone="warn">Unknown — confirm</StatusChip>
          ) : live && b.balanceDue > 0 ? (
            <StatusChip tone={overdue ? "danger" : "neutral"}>{overdue ? "Overdue " : "Due "}{formatCents(b.balanceDue)}</StatusChip>
          ) : (
            <Inline gap={1}>
              <Text as="span" variant="caption" tone="tertiary" tabular>
                {formatCents(b.total)}
              </Text>
            </Inline>
          )}
        </Stack>
      }
      onClick={onClick}
    />
  );
}
